import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { ensureSession } from '@/lib/engagementPoints';

export interface ThreadMessage {
  id: string;
  thread_id: string;
  sender_role: 'owner' | 'participant';
  message: string;
  created_at: string;
}

export interface ConversationThread {
  id: string;
  announcement_id: string;
  announcement_owner_id: string;
  participant_token: string;
  participant_user_id: string | null;
  status: 'active' | 'revealed' | 'blocked';
  created_at: string;
}

export interface RevealRequest {
  id: string;
  thread_id: string;
  requested_by: 'owner' | 'participant';
  owner_accepted: boolean | null;
  participant_accepted: boolean | null;
  resolved_at: string | null;
}

const TOKEN_KEY = 'sin_thread_token';

function getOrCreateToken(): string {
  try {
    let token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      token = crypto.randomUUID();
      try { localStorage.setItem(TOKEN_KEY, token); } catch {}
    }
    return token;
  } catch {
    // localStorage unavailable (private browsing, etc.)
    return crypto.randomUUID();
  }
}

export function useConversationThread(announcementId: string, isOwner: boolean) {
  const { user } = useAuth();
  const [thread, setThread] = useState<ConversationThread | null>(null);
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [revealRequest, setRevealRequest] = useState<RevealRequest | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const participantToken = getOrCreateToken();

  const getParticipantThreadSnapshot = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_thread_by_token' as any, {
      p_announcement_id: announcementId,
      p_participant_token: participantToken,
    });

    if (error) throw error;
    return (data as any) || { thread: null, messages: [] };
  }, [announcementId, participantToken]);

  const loadThread = useCallback(async () => {
    if (!announcementId) return;
    setIsLoading(true);

    try {
      if (isOwner && user && (await ensureSession())) {
        // Owner: load threads via direct query (authenticated)
        const { data } = await (supabase as any)
          .from('conversation_threads')
          .select('id, announcement_id, announcement_owner_id, participant_user_id, status, created_at, updated_at')
          .eq('announcement_id', announcementId)
          .eq('announcement_owner_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);
        if (data?.[0]) {
          setThread(data[0]);
          await loadMessagesAuth(data[0].id);
          await loadRevealRequest(data[0].id);
        }
      } else {
        // Participant: use RPC to load thread by token (works for anon)
        const result = await getParticipantThreadSnapshot();
        if (result) {
          if (result.thread) {
            setThread(result.thread);
            setMessages(result.messages || []);
            if (result.thread.id) {
              await loadRevealRequest(result.thread.id);
            }
          } else {
            setThread(null);
            setMessages([]);
            setRevealRequest(null);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load thread:', err);
    } finally {
      setIsLoading(false);
    }
  }, [announcementId, isOwner, user, getParticipantThreadSnapshot]);

  const loadMessagesAuth = async (threadId: string) => {
    const { data } = await (supabase as any)
      .from('thread_messages')
      .select('*')
      .eq('thread_id', threadId)
      .order('created_at', { ascending: true });
    if (data) setMessages(data);
  };

  const loadRevealRequest = async (threadId: string) => {
    const { data } = await (supabase as any)
      .from('reveal_requests')
      .select('*')
      .eq('thread_id', threadId)
      .is('resolved_at', null)
      .maybeSingle();
    if (data) setRevealRequest(data);
  };

  // Create thread + send first message (participant only)
  // Fallback: insert into announcement_responses if thread system fails
  const fallbackReply = useCallback(async (message: string): Promise<{ success: boolean; fallback: true } | null> => {
    if (!announcementId) return null;
    try {
      const { error } = await (supabase as any).from('announcement_responses').insert({
        announcement_id: announcementId,
        response_message: message.trim(),
        responder_name: null,
      });
      if (error) throw error;

      // Notify owner (fire-and-forget)
      const deviceId = (() => {
        try { return localStorage.getItem('sin_device_id') || crypto.randomUUID(); } catch { return crypto.randomUUID(); }
      })();
      try { localStorage.setItem('sin_device_id', deviceId); } catch {}

      supabase.functions.invoke('notify-reply', {
        body: { announcement_id: announcementId, responder_identifier: `fallback_${deviceId}` },
      }).catch(() => {});

      return { success: true, fallback: true };
    } catch (fbErr) {
      console.error('Fallback reply also failed:', fbErr);
      return null;
    }
  }, [announcementId]);

  // Create thread + send first message (participant only)
  const startThread = useCallback(async (message: string) => {
    if (!announcementId) return null;
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return null;

    setIsSending(true);
    try {
      const { data, error } = await supabase.rpc('create_thread_with_message' as any, {
        p_announcement_id: announcementId,
        p_participant_token: participantToken,
        p_message: trimmedMessage,
        p_participant_user_id: user?.id || null,
      });

      if (error) throw error;

      const result = data as any;
      if (!result?.success) throw new Error('Failed to create thread');

      // Notify owner (fire-and-forget)
      supabase.functions.invoke('notify-reply', {
        body: { announcement_id: announcementId, thread_id: result.thread_id },
      }).catch(err => console.warn('Reply notification failed:', err));

      // Reload thread data
      try { await loadThread(); } catch {}
      return result;
    } catch (err) {
      console.error('Failed to start thread:', err);

      const messageText = err instanceof Error ? err.message : String(err ?? '');

      // Recovery: thread already exists — send message to existing thread
      if (/thread already exists/i.test(messageText)) {
        try {
          const snapshot = await getParticipantThreadSnapshot();
          const existingThreadId = snapshot?.thread?.id;

          if (existingThreadId) {
            const { data, error } = await supabase.rpc('send_thread_message_as_participant' as any, {
              p_thread_id: existingThreadId,
              p_participant_token: participantToken,
              p_message: trimmedMessage,
            });
            if (error) throw error;

            try { await loadThread(); } catch {}
            return (data as any) || { thread_id: existingThreadId, success: true };
          }
        } catch (recoverErr) {
          console.error('Failed to recover existing thread send:', recoverErr);
        }
      }

      // Ultimate fallback: insert into announcement_responses (public table)
      console.warn('Thread system failed, attempting fallback reply via announcement_responses');
      const fallbackResult = await fallbackReply(trimmedMessage);
      if (fallbackResult) return fallbackResult;

      return null;
    } finally {
      setIsSending(false);
    }
  }, [announcementId, participantToken, user, loadThread, getParticipantThreadSnapshot, fallbackReply]);

  // Send message in existing thread
  const sendMessage = useCallback(async (message: string) => {
    if (!thread) return false;
    setIsSending(true);
    try {
      if (isOwner) {
        // Owner sends via direct insert (authenticated)
        const { error } = await (supabase as any).from('thread_messages').insert({
          thread_id: thread.id,
          sender_role: 'owner',
          message: message.trim(),
        });
        if (error) throw error;
      } else {
        // Participant sends via RPC (works for anon)
        const { data, error } = await supabase.rpc('send_thread_message_as_participant' as any, {
          p_thread_id: thread.id,
          p_participant_token: participantToken,
          p_message: message.trim(),
        });
        if (error) throw error;
      }

      // Reload messages
      if (isOwner) {
        await loadMessagesAuth(thread.id);
      } else {
        // Reload via RPC
        const { data } = await supabase.rpc('get_thread_by_token' as any, {
          p_announcement_id: thread.announcement_id,
          p_participant_token: participantToken,
        });
        if (data) setMessages((data as any).messages || []);
      }

      // Notify owner when participant replies
      if (!isOwner) {
        supabase.functions.invoke('notify-reply', {
          body: { announcement_id: thread.announcement_id, thread_id: thread.id },
        }).catch(err => console.warn('Reply notification failed:', err));
      }

      return true;
    } catch (err) {
      console.error('Failed to send message:', err);
      return false;
    } finally {
      setIsSending(false);
    }
  }, [thread, isOwner, participantToken]);

  // Request reveal
  const requestReveal = useCallback(async () => {
    if (!thread) return false;
    try {
      const requestedBy = isOwner ? 'owner' : 'participant';
      
      if (revealRequest) {
        const update: Record<string, any> = {};
        if (isOwner) update.owner_accepted = true;
        else update.participant_accepted = true;

        const { data } = await (supabase as any)
          .from('reveal_requests')
          .update(update)
          .eq('id', revealRequest.id)
          .select()
          .single();

        if (data) {
          setRevealRequest(data);
          if (data.owner_accepted && data.participant_accepted) {
            await (supabase as any).from('reveal_requests')
              .update({ resolved_at: new Date().toISOString() })
              .eq('id', data.id);
            await (supabase as any).from('conversation_threads')
              .update({ status: 'revealed' })
              .eq('id', thread.id);
            
            if (user && thread.participant_user_id) {
              await (supabase as any).from('connections').insert({
                thread_id: thread.id,
                user_a_id: thread.announcement_owner_id,
                user_b_id: thread.participant_user_id,
              });
            }
            setThread(prev => prev ? { ...prev, status: 'revealed' } : null);
          }
        }
        return true;
      }

      const insertData: Record<string, any> = {
        thread_id: thread.id,
        requested_by: requestedBy,
      };
      if (isOwner) insertData.owner_accepted = true;
      else insertData.participant_accepted = true;

      const { data } = await (supabase as any)
        .from('reveal_requests')
        .insert(insertData)
        .select()
        .single();

      if (data) setRevealRequest(data);
      return true;
    } catch (err) {
      console.error('Failed to request reveal:', err);
      return false;
    }
  }, [thread, isOwner, revealRequest, user]);

  // Subscribe to realtime messages
  useEffect(() => {
    if (!thread) return;
    const channel = supabase
      .channel(`thread-${thread.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'thread_messages',
        filter: `thread_id=eq.${thread.id}`,
      }, (payload) => {
        setMessages(prev => [...prev, payload.new as ThreadMessage]);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [thread?.id]);

  useEffect(() => { loadThread(); }, [loadThread]);

  return {
    thread,
    messages,
    revealRequest,
    isLoading,
    isSending,
    startThread,
    sendMessage,
    requestReveal,
    loadThread,
  };
}
